import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpService } from '@services/http.service';
import { LookupsService } from '@services/lookups.service';
import {
  PHARMACY_DETAIL_INCLUDE_ENROLLMENTS,
  PharmacyDetailModel,
  PharmacyDetailOptionsModel,
  PharmacyDetailResponseModel,
  PharmacyEnrollmentModel,
  PharmacyEnrollmentUpdateRequestModel,
  PharmacyOnboardingInstructionsRequestModel,
  PharmacyRequestModel,
  PharmacySaveResponseModel,
} from '@services/models/pharmacy';
import { PharmacyFamilyModel } from '@shared/models/pharmacy-family-model';
import { catchError, map, mergeMap, Observable, of } from 'rxjs';

// HZN enrollments are hidden from the pharmacy enrollment screens
const HIDDEN_ENROLLMENT_TENANT_IDS = ['HZN'];

@Injectable({ providedIn: 'root' })
export class PharmacyService extends HttpService {
  private readonly lookupsService = inject(LookupsService);

  get(ncpdp: string, options: PharmacyDetailOptionsModel = {}): Observable<PharmacyDetailModel | undefined> {
    return this.getDetail(`${this.urlService.PA_PHARMACY_URL}/${encodeURIComponent(ncpdp)}`, options);
  }

  getByNpi(npi: string, options: PharmacyDetailOptionsModel = {}): Observable<PharmacyDetailModel | undefined> {
    return this.getDetail(`${this.urlService.PA_PHARMACY_BY_NPI_URL}/${encodeURIComponent(npi)}`, options);
  }

  getWithEnrollments(ncpdp: string): Observable<PharmacyDetailModel | undefined> {
    return this.get(ncpdp, { include: [PHARMACY_DETAIL_INCLUDE_ENROLLMENTS] }).pipe(
      map(pharmacy =>
        pharmacy
          ? {
              ...pharmacy,
              enrollments: (pharmacy.enrollments ?? []).filter(enrollment => !HIDDEN_ENROLLMENT_TENANT_IDS.includes(enrollment.tenant.id)),
            }
          : undefined,
      ),
    );
  }

  create(data: PharmacyRequestModel): Observable<PharmacySaveResponseModel> {
    return this.httpClient.post<PharmacySaveResponseModel>(this.urlService.MASTER_DATA_PHARMACIES_URL, data);
  }

  update(ncpdp: string, data: PharmacyRequestModel): Observable<PharmacySaveResponseModel> {
    return this.httpClient.patch<PharmacySaveResponseModel>(this.createMasterDataItemUrl(ncpdp), data);
  }

  sendOnboardingInstructions(ncpdp: string, data: PharmacyOnboardingInstructionsRequestModel): Observable<PharmacySaveResponseModel> {
    return this.httpClient.post<PharmacySaveResponseModel>(`${this.createMasterDataItemUrl(ncpdp)}/onboarding-instructions`, data);
  }

  updateEnrollment(ncpdp: string, tenantId: string, data: PharmacyEnrollmentUpdateRequestModel): Observable<PharmacyEnrollmentModel> {
    return this.httpClient
      .patch<{
        enrollment: PharmacyEnrollmentModel;
      }>(`${this.urlService.PA_PHARMACY_URL}/${encodeURIComponent(ncpdp)}/enrollments/${encodeURIComponent(tenantId)}`, data)
      .pipe(map(response => response.enrollment));
  }

  // mirrors the S3 key naming of the master-data pharmacy payment info provider
  getPaymentInfoAttachmentKey(pharmacyNpi: string, fileName: string, type: 'authorization' | 'bankLetter'): string {
    return type === 'bankLetter' ? fileName : `pharmacy_detail_${pharmacyNpi}_${fileName}`;
  }

  getPharmacyFamilies(): Observable<PharmacyFamilyModel[]> {
    return this.lookupsService.getSettings<PharmacyFamilyModel[]>('pharmacy', 'family').pipe(map(value => value ?? []));
  }

  setPharmacyFamilies(families: PharmacyFamilyModel[]) {
    return this.lookupsService.setSettings('pharmacy', 'family', families);
  }

  getPharmacyFamily(familyId: string): Observable<PharmacyFamilyModel | undefined> {
    return this.lookupsService
      .getSettings<PharmacyFamilyModel[]>('pharmacy', 'family')
      .pipe(map(value => (value ?? []).find(family => family.id === familyId)));
  }

  setPharmacyFamily(family: PharmacyFamilyModel) {
    return this.lookupsService.getSettings<PharmacyFamilyModel[]>('pharmacy', 'family').pipe(
      mergeMap(families => {
        if (!families) {
          return of(undefined);
        }
        const originalIndex = families.findIndex(f => f.id === family.id);
        if (originalIndex !== -1) {
          families[originalIndex] = family;
        } else {
          families.push(family);
        }
        return this.setPharmacyFamilies(families);
      }),
    );
  }

  private getDetail(url: string, { include, tenantIds }: PharmacyDetailOptionsModel): Observable<PharmacyDetailModel | undefined> {
    let params = new HttpParams();
    if (include?.length) params = params.set('include', include.join(','));
    if (tenantIds?.length) params = params.set('tenantIds', tenantIds.join(','));

    return this.httpClient.get<PharmacyDetailResponseModel>(url, { params }).pipe(
      map(({ pharmacy, ...related }) => ({ ...pharmacy, ...related })),
      catchError(error => this.handleGetError(error)),
    );
  }

  private createMasterDataItemUrl(ncpdp: string): string {
    return `${this.urlService.MASTER_DATA_PHARMACIES_URL}/${encodeURIComponent(ncpdp)}`;
  }
}
